import { I18n } from "aws-amplify/utils";

/**
 * Mirrors the Cognito user pool's password policy, and rewrites the policy
 * errors Cognito returns into copy a user can act on.
 *
 * Kept in step with carrot-web (js-mono `src/ui/screens/SignIn`), which shows
 * the same requirements and the same messages.
 */

/**
 * Passed to <Authenticator passwordSettings>, which is what makes Amplify check
 * the length locally before calling Cognito. It must not be looser than the
 * pool's own policy, or the user gets a server-side rejection with no field-level
 * error. Cognito remains the source of truth; this is only for fast feedback.
 *
 * 15 is the `staging-main` pool's minimum; production is being brought in line
 * with it. Neither pool requires particular character classes, so length is the
 * only local check. Raising a pool's minimum needs no change here; lowering one
 * below 15 does.
 */
export const PASSWORD_POLICY = { minLength: 15 };

/* Worded identically to Amplify's built-in validator output so the user sees the
 * same sentence whether the length check runs locally or comes back from Cognito.
 */
export const PASSWORD_MIN_LENGTH_MESSAGE = `Password must have at least ${PASSWORD_POLICY.minLength} characters`;

export const PASSWORD_REQUIREMENTS_HEADING = "Your password must be:";

export const PASSWORD_REQUIREMENTS = [
  `At least ${PASSWORD_POLICY.minLength} characters long`,
  "Not a common password, and not one found in a known data breach"
];

/* Verbatim messages returned by Cognito, used as I18n lookup keys to replace them
 * with the copy below. Threat protection produces `compromised`; the `Legacy`
 * variant is the older wording, which some Cognito APIs still return.
 */
export const COGNITO_PASSWORD_MESSAGES = {
  compromised: "Provided password cannot be used for security reasons.",
  notLongEnough:
    "Password does not conform to policy: Password not long enough",
  notLongEnoughLegacy:
    "Password did not conform with policy: Password not long enough"
};

export const CUSTOM_ERROR_MESSAGES = {
  compromisedPassword:
    "This password appears in a known data breach or is too easily guessed. Please choose a different password."
};

/**
 * Rewrites Cognito's password errors. The Authenticator renders every remote
 * error through `I18n.get`, so an entry keyed by Cognito's exact message
 * replaces it. Call once, before the Authenticator renders.
 */
export const configureAmplifyI18n = (): void => {
  /* I18n otherwise resolves the language from the browser, which would miss these
   * entries for non-English locales. All Authenticator copy is English regardless,
   * since the Amplify translation dictionaries are never loaded.
   */
  I18n.setLanguage("en");

  I18n.putVocabulariesForLanguage("en", {
    [COGNITO_PASSWORD_MESSAGES.compromised]:
      CUSTOM_ERROR_MESSAGES.compromisedPassword,
    [COGNITO_PASSWORD_MESSAGES.notLongEnough]: PASSWORD_MIN_LENGTH_MESSAGE,
    [COGNITO_PASSWORD_MESSAGES.notLongEnoughLegacy]: PASSWORD_MIN_LENGTH_MESSAGE
  });
};
